import {
  Button,
  ConfirmationModalContent,
  LoadingSpinner,
  ModalPopup,
  Stack,
  Text
} from 'components';
import { isDateTodayOrPast } from 'components/datePicker/dateTimepopover/dateAndTimePopover';
import AudioAssist from 'components/ocrAudio/audioAssist';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { FormikProvider, useFormik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import debounce from 'lodash.debounce';
import { removeCurrentNote, setCurrentNoteID } from 'modules/networkTools/localStorageTokens';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  deleteNoteInput,
  getClientNote,
  getClientNotes,
  getUsersImageNotes,
  postCreateNote,
  putUpdateNote
} from 'store/clientProfile/clientProfileActions';
import { ClientProfileContentItem, removeNote } from 'store/clientProfile/clientProfileSlice';
import { typesOfSupportData } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getTodos as getMyTodos } from 'store/myTodos/myTodosActions';
import { getTodos as getRoadmapTodos } from 'store/roadmaps/roadmapsActions';
import { notifyUserSuccess } from 'utils/notifications';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { useIsOnlyVisibleToUserWithPermissions } from 'utils/hooks/useIsOnlyVisibleTo';
import { PERMISSION } from 'utils/protectedRoutes/userRolesAndPermissionTypes';

import { LastSaved } from '../noteView/addendumForm/lastSaved';
import style from './noteEdit.module.scss';
import Transcript from './transcript/transcript';
import { AudioPlayer } from './audioPlayer';
import {
  NoteType,
  DateOfEncounter,
  StartTime,
  TimeSpent,
  NextContact,
  WhoDidYouReach,
  TypeOfSupport,
  Note,
  DocumentationTime
} from './fields';
import Manager from './fields/manager/manager';
import Chw from './fields/chw/chw';
import Provider from './fields/provider/provider';
dayjs.extend(isSameOrAfter);

export type Props = {
  note?: ClientProfileContentItem;
  clientId: string;
  isEditMode?: boolean;
  inputOpen?: boolean;
  noteId?: string;
  context: 'popup' | 'noteSection';
};

const isValidDateOrTimeOrNull = (value: any, type: 'date' | 'time') => {
  const parsed = type === 'date' ? dayjs(value) : dayjs(value, ['hh:mm A', 'HH:mm:ss'], true);

  const isValid = parsed.isValid();

  if (isValid) {
    if (type === 'date' && !isDateTodayOrPast(value)) return null;
    return value;
  } else {
    return null;
  }
};

export function useIsFirstRender(): boolean {
  const isFirst = useRef(true);

  if (isFirst.current) {
    isFirst.current = false;

    return true;
  }

  return isFirst.current;
}

const useFormikOnChange = (values: any, callback: (values: any) => void) => {
  const isFirstRender = useIsFirstRender();

  useEffect(() => {
    if (isFirstRender) return;
    callback(values);
  }, [values]);
};

const defaultValues = {
  uuid: uuidv4(),
  note: null,
  draft: null,
  noteType: null,
  clientReached: null,
  dateOfEncounter: null,
  startTimeOfEncounter: null,
  nextContact: null,
  typeOfSupport: [],
  id: null,
  timeSpentMin: null,
  timeSpent: null,
  siteId: null,
  zCodes: [],
  whoDidYouReach: [],
  whoWereYouTryingToReach: '',
  howDidYouTryThem: '',
  howDidYouReachThem: '',
  whatTypeOfPlaceWereYouIn: '',
  billingProviderId: '',
  otherProviderName: '',
  documentationTime: null,
  chwId: null,
  managerId: null
};

const validationSchema = Yup.object({
  note: Yup.string().required('Please enter a note to save.').nullable(),
  noteType: Yup.string().required('Note type is required.').nullable(),
  dateOfEncounter: Yup.string()
    .nullable()
    .required('Date of Encounter is required.')
    .test(
      'dateOfEncounter',
      "Date of Encounter can't be in the future.",
      async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
        if (!fieldVal) return false;

        // Validate the date format
        const parsedDate = dayjs(fieldVal, 'MM/DD/YYYY', true);
        if (!parsedDate.isValid()) {
          return false;
        }

        // Check if date is today or in the past
        if (parsedDate.isAfter(dayjs(), 'day')) {
          return false;
        }

        return true; // If all checks pass, validation succeeds
      }
    ),

  startTimeOfEncounter: Yup.string()
    .nullable()
    .when('noteType', {
      is: (noteType: string) => noteType !== 'MANAGER_REVIEW',
      then: Yup.string()
        .nullable()
        .test(
          'startTimeOfEncounter',
          'Start Time is required.',
          async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
            if (!fieldVal || fieldVal === 'Invalid Date') return false;

            return true;
          }
        ),
      otherwise: Yup.string().nullable().notRequired()
    }),
  nextContact: Yup.string()
    .nullable()
    .test(
      'nextContact',
      'Date of Next Contact must be after Date of Encounter.',
      async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
        const formikValues = values.parent;

        if (fieldVal == null || fieldVal === '') {
          return true;
        }

        if (formikValues.dateOfEncounter == null || formikValues.dateOfEncounter === '') {
          return true;
        }
        //check if end time after start time
        let isStartTimeBeforeEndTime = true;
        const startTime = dayjs(`${formikValues.dateOfEncounter}`);

        const endTime = dayjs(`${formikValues.nextContact}`);

        isStartTimeBeforeEndTime = startTime.isBefore(endTime);

        return isStartTimeBeforeEndTime;
      }
    ),

  typeOfSupport: Yup.array()
    .of(Yup.string())
    .when('noteType', {
      is: (noteType: string) => noteType !== 'MANAGER_REVIEW',
      then: Yup.array()
        .of(Yup.string())
        .min(1, 'Type of support is required.')
        .required('Type of support is required.'),
      otherwise: Yup.array().of(Yup.string()).notRequired()
    }),
  timeSpentMin: Yup.string().nullable().required('Time Spent (min) is required.'),
  documentationTime: Yup.string()
    .nullable()
    .required('Documentation Time (min) is required.')
    .test('documentationTime', 'Documentation Time (min) is required.', (value) => {
      return value !== null && value !== '' && parseInt(value ?? '0') > 0;
    }),
  whoDidYouReach: Yup.array()
    .of(Yup.string())
    .when('noteType', {
      is: (noteType: string) => noteType !== 'MANAGER_REVIEW',
      then: Yup.array()
        .of(Yup.string())
        .min(1, 'Who did you reach? is required.')
        .required('Who did you reach? is required.'),
      otherwise: Yup.array().of(Yup.string()).notRequired()
    }),
  howDidYouReachThem: Yup.string().when('whoDidYouReach', {
    is: (whoDidYouReach: string[]) =>
      whoDidYouReach.length > 0 && !whoDidYouReach.includes('NO_ONE'),
    then: Yup.string().required('How did you reach them? is required.'),
    otherwise: Yup.string().notRequired()
  }),
  otherProviderName: Yup.string().when('billingProviderId', {
    is: (billingProviderId: string) => billingProviderId === '-1',
    then: Yup.string().required('Provider Name is required.'),
    otherwise: Yup.string().notRequired()
  }),
  whoWereYouTryingToReach: Yup.string().when('whoDidYouReach', {
    is: (whoDidYouReach: string[]) => whoDidYouReach.includes('NO_ONE'),
    then: Yup.string()
      .min(1, 'Who were you trying to reach? is required.')
      .required('Who were you trying to reach? is required.'),
    otherwise: Yup.string().notRequired()
  }),
  howDidYouTryThem: Yup.string().when(['whoWereYouTryingToReach', 'whoDidYouReach', 'noteType'], {
    is: (whoWereYouTryingToReach: string, whoDidYouReach: string[], noteType: string) => {
      return (
        whoWereYouTryingToReach !== '' &&
        whoWereYouTryingToReach != null &&
        whoWereYouTryingToReach !== 'WORK_ON_MY_CLIENTS_BEHALF'
      );
    },
    then: Yup.string().required('How did you try them? is required.'),
    otherwise: Yup.string().notRequired()
  }),
  whatTypeOfPlaceWereYouIn: Yup.string().when(['howDidYouTryThem', 'howDidYouReachThem'], {
    is: (howDidYouTryThem: string, howDidYouReachThem: string) => {
      return howDidYouTryThem === 'IN_PERSON' || howDidYouReachThem === 'IN_PERSON';
    },
    then: Yup.string().required('What type of place were you in? is required.'),
    otherwise: Yup.string().notRequired()
  }),
  siteId: Yup.number()
    .nullable()
    .when(['whatTypeOfPlaceWereYouIn', 'sitesEnabled', 'whoDidYouReach'], {
      is: (whatTypeOfPlaceWereYouIn: string, sitesEnabled: boolean, whoDidYouReach: string[]) => {
        return (
          whatTypeOfPlaceWereYouIn !== '' &&
          whatTypeOfPlaceWereYouIn != null &&
          whatTypeOfPlaceWereYouIn !== 'CLIENT_HOME' &&
          sitesEnabled &&
          !whoDidYouReach.includes('NO_ONE')
        );
      },
      then: Yup.number().nullable().required('Site is required'),
      otherwise: Yup.number().nullable().notRequired()
    }),
  zCodes: Yup.array()
    .of(
      Yup.object({
        id: Yup.number().nullable().required('SDOH Z codes is required'),
        selectedExplanations: Yup.array()
          .of(Yup.number())
          .min(1, 'Explain SDOH Z codes is required.')
          .required('Explain SDOH Z codes is required.')
      })
    )
    .when(['typeOfSupport', 'isBillingEnabled'], {
      is: (typeOfSupport: string[], isBillingEnabled: boolean) =>
        typeOfSupport.some((type) => type !== 'NONE') && isBillingEnabled,
      then: (schema) =>
        schema.min(1, 'SDOH Z codes is required.').required('SDOH Z codes is required.')
    }),
  managerId: Yup.string()
    .nullable()
    .when('noteType', {
      is: (noteType: string) => noteType === 'MANAGER_REVIEW',
      then: Yup.string().nullable().required('Manager is required.'),
      otherwise: Yup.string().nullable().notRequired()
    }),
  chwId: Yup.string()
    .nullable()
    .when('noteType', {
      is: (noteType: string) => noteType === 'MANAGER_REVIEW',
      then: Yup.string().nullable().required('CHW is required.'),
      otherwise: Yup.string().nullable().notRequired()
    }),
  billingProviderId: Yup.string()
    .nullable()
    .test('billingProviderId', 'Provider is required.', (value, context) => {
      const noteType = context.parent.noteType;
      const isBillingEnabled = context.parent.isBillingEnabled;
      const whoDidYouReach = context.parent.whoDidYouReach;
      if (noteType === 'MANAGER_REVIEW' && isBillingEnabled && (value === '' || value == null)) {
        return false;
      }
      if (whoDidYouReach.includes('PROVIDER') && (value === '' || value == null)) {
        return false;
      }

      return true;
    })
});

const AiScribeIcon = () => {
  return (
    <svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17.1065 7.00002L17.8965 5.25002L19.6465 4.46002C20.0365 4.28002 20.0365 3.73002 19.6465 3.55002L17.8965 2.76002L17.1065 1.00002C16.9265 0.61002 16.3765 0.61002 16.1965 1.00002L15.4065 2.75002L13.6465 3.54002C13.2565 3.72002 13.2565 4.27002 13.6465 4.45002L15.3965 5.24002L16.1865 7.00002C16.3665 7.39002 16.9265 7.39002 17.1065 7.00002ZM9.14652 8.50002L7.55652 5.00002C7.20652 4.22002 6.08652 4.22002 5.73652 5.00002L4.14652 8.50002L0.646523 10.09C-0.133477 10.45 -0.133477 11.56 0.646523 11.91L4.14652 13.5L5.73652 17C6.09652 17.78 7.20652 17.78 7.55652 17L9.14652 13.5L12.6465 11.91C13.4265 11.55 13.4265 10.44 12.6465 10.09L9.14652 8.50002ZM16.1865 15L15.3965 16.75L13.6465 17.54C13.2565 17.72 13.2565 18.27 13.6465 18.45L15.3965 19.24L16.1865 21C16.3665 21.39 16.9165 21.39 17.0965 21L17.8865 19.25L19.6465 18.46C20.0365 18.28 20.0365 17.73 19.6465 17.55L17.8965 16.76L17.1065 15C16.9265 14.61 16.3665 14.61 16.1865 15Z"
        fill="#188AA1"
      />
    </svg>
  );
};

const remapValues = ({
  values,
  clientId,
  id,
  draft
}: {
  values: any;
  clientId: string;
  id?: string;
  draft: boolean;
}) => {
  return {
    clientId: clientId,
    id,
    draft,
    eventTimestamp: new Date().getTime(),
    uuid: values.uuid,
    note: values.note,
    noteType: values.noteType || null,
    clientReached: values.clientReached || null,
    dateOfEncounter: isValidDateOrTimeOrNull(values.dateOfEncounter, 'date'),
    startTimeOfEncounter: isValidDateOrTimeOrNull(values.startTimeOfEncounter, 'time'),
    nextContact: values.nextContact || null,
    typeOfSupport: values.typeOfSupport || null,
    timeSpentMin: values.timeSpentMin,
    siteId: values.siteId ?? null,
    zCodes:
      values.zCodes?.map((z: any) => ({
        ...z,
        selectedExplanations: z.selectedExplanations?.map((e: any) => ({ id: Number(e) })) ?? []
      })) ?? [],
    chwId: values.chwId ?? null,
    billingProviderId: values.billingProviderId ?? null,
    managerId: values.managerId ?? null,
    documentationTime: values.documentationTime ?? null,
    otherProviderName: values.otherProviderName ?? '',
    whoDidYouReach: values.whoDidYouReach ?? [],
    howDidYouReachThem: values.howDidYouReachThem ?? '',
    whoWereYouTryingToReach: values.whoWereYouTryingToReach ?? '',
    howDidYouTryThem: values.howDidYouTryThem ?? '',
    whatTypeOfPlaceWereYouIn: values.whatTypeOfPlaceWereYouIn ?? ''
  };
};

const sortTypesOfSupportValues = (val: string[] | string, sortOrder: any[]): string[] => {
  if (Array.isArray(val)) {
    return sortOrder.filter((item) => val.includes(item.id.toString())).map((item) => item.id);
  } else {
    return [val];
  }
};

function removeEmptyFieldsFromObject<T extends Record<string, any>>(obj: T): T {
  const filteredEntries = Object.entries(obj).filter(
    ([_, value]) => value !== undefined && value !== ''
  );
  return Object.fromEntries(filteredEntries) as unknown as T;
}

const NewNoteEdit = ({ note, clientId, inputOpen, noteId, context }: Props) => {
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [formDisabled, setFormDisabled] = useState(context === 'popup');
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { size } = useAppSelector((state) => state.clientProfile.clientProfileList);
  const [searchParams] = useSearchParams();
  const noteTypes = searchParams.get('noteTypes');
  const { enableAudioAssist, aiScribe: enableAiScribe } = useFlags();
  const isBillingEnabled = useAppSelector((state) => state.clients.client.data?.enableBilling);
  const isTranscribing =
    note?.aiScribe?.status === 'TRANSCRIBE_STARTED' ||
    note?.aiScribe?.status === 'TRANSCRIBE_IN_PROGRESS' ||
    note?.aiScribe?.status === 'TRANSCRIBE_COMPLETED' ||
    note?.aiScribe?.status === 'SUMMARIZING';
  const memoizedUuid = useMemo(() => uuidv4(), []);
  const sitesEnabled = useAppSelector((state) => state.clients.client.data?.enableProgramSites);
  const canUpdateManagerReview = useIsOnlyVisibleToUserWithPermissions([
    PERMISSION.UPDATE_MANAGER_REVIEW
  ]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isAutosaving && context === 'popup') {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAutosaving, context]);

  const debouncedNoteAutoSave = useRef(
    debounce(
      async (formikValues, id, types) => {
        if (formikValues === null) return;
        setIsAutosaving(true);
        if (id) {
          const dataUpdate = remapValues({ values: formikValues, clientId, id, draft: true });
          const res = await dispatch(putUpdateNote(removeEmptyFieldsFromObject(dataUpdate)));

          if (res.meta.requestStatus === 'fulfilled') {
            setLastSaved(new Date().toISOString());
            const channel = new BroadcastChannel('note-update');
            channel.postMessage({ message: 'EDIT_NOTE', data: dataUpdate });

            if (formikValues.noteType === 'SAFETY_NOTE' || note?.noteType === 'SAFETY_NOTE') {
              channel.postMessage({ message: 'SAFETY_NOTE' });
            }
            channel.close();
          }
        } else {
          const dataCreate = remapValues({ values: formikValues, clientId, draft: true });
          const res = await dispatch(postCreateNote(removeEmptyFieldsFromObject(dataCreate)));

          if (res.meta.requestStatus === 'fulfilled') {
            setCurrentNoteID({ currentNoteID: res.payload?.id, clientId });
            setLastSaved(new Date().toISOString());
            const channel = new BroadcastChannel('note-update');
            if (formikValues.noteType === 'SAFETY_NOTE' || note?.noteType === 'SAFETY_NOTE') {
              channel.postMessage({ message: 'SAFETY_NOTE' });
            }
            channel.close();
            await dispatch(
              getClientNotes({
                clientId,
                page: 0,
                size,
                types,
                firstRender: true
              })
            ).then((result: any) => {
              if (result.payload.data.content.length > 0) {
                //TODO need to optimize
                result.payload.data.content.map((noteItem: ClientProfileContentItem) => {
                  if (noteItem.author.profileImageKey) {
                    dispatch(
                      getUsersImageNotes({
                        profileImageKey: noteItem.author.profileImageKey,
                        userId: clientId,
                        noteId: noteItem.id.toString(),
                        notesList: true
                      })
                    );
                  }
                });
              }
            });
          }
        }
        setIsAutosaving(false);
      },
      context === 'popup' ? 300 : 1000
    )
  ).current;

  const formik = useFormik({
    initialValues: {
      note: note?.note || null,
      draft: note?.draft || null,
      noteType: note?.noteType || null,
      clientReached: note?.clientReached || null,
      dateOfEncounter: note?.dateOfEncounter || null,
      startTimeOfEncounter: note?.startTimeOfEncounter || null,
      nextContact: note?.nextContact || null,
      typeOfSupport: note?.typeOfSupport
        ? sortTypesOfSupportValues(note.typeOfSupport, typesOfSupportData)
        : [],
      id: note?.id || null,
      timeSpentMin: note?.timeSpentMin || null,
      siteId: note?.siteId || null,
      uuid: note?.uuid ?? memoizedUuid,
      zCodes:
        note?.selectedZCodes?.map((z) => ({
          id: z.id,
          name: `${z.code} - ${z.description}`,
          explanations: z.explanations,
          selectedExplanations: z.selectedExplanations?.map((e: any) => e.id.toString()) || []
        })) || [],
      whoDidYouReach: note?.whoDidYouReach || [],
      whoWereYouTryingToReach: note?.whoWereYouTryingToReach || '',
      howDidYouTryThem: note?.howDidYouTryThem || '',
      howDidYouReachThem: note?.howDidYouReachThem || null,
      whatTypeOfPlaceWereYouIn: note?.whatTypeOfPlaceWereYouIn || '',
      billingProviderId: note?.billingProviderId?.toString() || null,
      otherProviderName: note?.otherProviderName || '',
      documentationTime: note?.documentationTime || null,
      sitesEnabled: sitesEnabled,
      isBillingEnabled: isBillingEnabled,
      chwId: note?.chwId || null,
      managerId: note?.managerId || null
    },
    validateOnMount: true,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (noteId) {
        const dataUpdate = remapValues({ values, clientId, id: noteId, draft: false });
        const res = await dispatch(putUpdateNote(removeEmptyFieldsFromObject(dataUpdate)));

        if (res.meta.requestStatus === 'fulfilled') {
          const channel = new BroadcastChannel('note-update');
          channel.postMessage({ message: 'EDIT_NOTE', data: dataUpdate });
          if (values.noteType === 'SAFETY_NOTE' || note?.noteType === 'SAFETY_NOTE') {
            channel.postMessage({ message: 'SAFETY_NOTE' });
          }
          if (values.nextContact != null && values.nextContact !== '') {
            notifyUserSuccess('Next contact to-do created');
            if (context === 'popup') {
              channel.postMessage({ message: 'NEXT_CONTACT_TODO' });
            } else {
              dispatch(getMyTodos());
              dispatch(getRoadmapTodos({ clientId }));
            }
          }
          channel.close();
          removeCurrentNote();
          setLastSaved(null);
          notifyUserSuccess('Note saved.');
          resetForm({
            values: { ...defaultValues, sitesEnabled, isBillingEnabled }
          });
          dispatch(removeNote());

          if (context === 'popup') {
            await dispatch(
              getClientNote({
                clientId,
                noteId
              })
            );
          }
        }
      }
    }
  });

  useFormikOnChange(formik.values, (values) => {
    if (!Boolean(values.note)) return;
    if (formik.isSubmitting) return;
    debouncedNoteAutoSave(values, noteId, noteTypes);
  });

  const deleteNote = () => {
    formik.resetForm({
      values: { ...defaultValues, sitesEnabled, isBillingEnabled }
    });
    setLastSaved(null);
    dispatch(removeNote());
    setShowConfirmationModal(false);

    if (noteId) {
      dispatch(
        deleteNoteInput({
          clientId,
          noteId
        })
      ).then(() => {
        removeCurrentNote();
        const channel = new BroadcastChannel('note-update');
        channel.postMessage({ message: 'DELETE_NOTE', noteId });
        if (note?.noteType === 'SAFETY_NOTE') {
          channel.postMessage({ message: 'SAFETY_NOTE' });
        }
        window.close();
      });
    }
  };

  const isManagerReview = formik.values.noteType === 'MANAGER_REVIEW';

  return (
    <Stack width="100%" mt="16px" display="flex" gap="28px" data-test-id="profile-notes-form">
      <FormikProvider value={formik}>
        <form
          onSubmit={formik.handleSubmit}
          id="noteEdit"
          onReset={() => {
            formik.resetForm({
              values: { ...defaultValues, sitesEnabled, isBillingEnabled }
            });
            removeCurrentNote();
            dispatch(removeNote());
            setLastSaved(null);
          }}
        >
          {inputOpen && (
            <>
              <div className={style.inputRow}>
                <div className={style.halfRow}>
                  <NoteType disabled={formDisabled} />
                </div>
                <div className={style.halfRow}>
                  <DateOfEncounter disabled={formDisabled} />
                </div>
              </div>
              {!isManagerReview && (
                <div className={style.inputRow}>
                  <div className={style.halfRow}>
                    <StartTime disabled={formDisabled} />
                  </div>
                  <div className={style.halfRow}>
                    <TimeSpent disabled={formDisabled} />
                  </div>
                </div>
              )}
              {isManagerReview && (
                <div className={style.inputRow}>
                  <div className={style.halfRow}>
                    <TimeSpent disabled={formDisabled} />
                  </div>
                  <div className={style.halfRow}>
                    <Manager disabled={formDisabled} />
                  </div>
                </div>
              )}
              {isManagerReview && (
                <div className={style.inputRow}>
                  <div className={style.halfRow}>
                    <Chw disabled={formDisabled} />
                  </div>
                  <div className={style.halfRow}>
                    <Provider disabled={formDisabled} required={Boolean(isBillingEnabled)} />
                  </div>
                </div>
              )}
              {!isManagerReview && (
                <div className={style.inputRow}>
                  <div className={style.halfRow}>
                    <NextContact disabled={formDisabled} />
                  </div>
                </div>
              )}
              {!isManagerReview && (
                <div className={style.inputRow}>
                  <div className={style.halfRow}>
                    <WhoDidYouReach disabled={formDisabled} />
                  </div>
                </div>
              )}
              {!isManagerReview && (
                <Stack width="100%" mb="4px">
                  <TypeOfSupport disabled={formDisabled} />
                </Stack>
              )}
            </>
          )}
          {enableAiScribe && isTranscribing && (
            <Stack
              width="100%"
              mb="16px"
              className={style.aiScribeInProgress}
              gap="8px"
              display="flex"
              flexDirection="row"
              padding="12px"
              alignItems="center"
              boxSizing="border-box"
              borderRadius="8px"
            >
              <AiScribeIcon />
              <Text weight="medium" size="normal" className={style.aiScribeInProgressText}>
                AI Scribe is processing your note, you’ll be able to edit when it's done. Check back
                soon!
              </Text>
            </Stack>
          )}
          {enableAiScribe &&
            note?.aiScribe?.status === 'FAILED' &&
            note?.aiScribe?.errorMessage && (
              <Stack
                width="100%"
                mb="16px"
                className={style.aiScribeError}
                gap="8px"
                display="flex"
                flexDirection="row"
                padding="12px"
                alignItems="center"
                boxSizing="border-box"
                borderRadius="8px"
              >
                <span className="material-icons">error_outline</span>
                <Text weight="medium" size="normal" className={style.aiScribeErrorText}>
                  {note.aiScribe.errorMessage}
                </Text>
              </Stack>
            )}
          {context === 'popup' &&
            enableAiScribe &&
            note?.aiScribe?.status === 'FAILED' &&
            !note?.aiScribe?.transcript &&
            note?.aiScribe?.audioUrl && <AudioPlayer note={note} />}

          <div className={style.halfRow}>
            <Note disabled={formDisabled} />
          </div>
          <div className={style.halfRow}>
            <DocumentationTime disabled={formDisabled} />
          </div>
          <div className={style.saveOrDiscard}>
            <div className={style.btnWrap}>
              <Button
                text=""
                type="button"
                disabled={
                  (enableAiScribe && isTranscribing) ||
                  (!canUpdateManagerReview && formik.values.noteType === 'MANAGER_REVIEW')
                }
                icon={<span className={'material-icons'}>delete</span>}
                handleOnClick={() => setShowConfirmationModal(true)}
                customStyleClass={style.actionButton}
              />
              {context === 'popup' && (
                <Button
                  text=""
                  type="button"
                  customStyleClass={style.actionButton}
                  disabled={
                    (enableAiScribe && isTranscribing) ||
                    (!canUpdateManagerReview && formik.values.noteType === 'MANAGER_REVIEW')
                  }
                  icon={<span className={'material-icons'}>edit</span>}
                  handleOnClick={() => setFormDisabled(false)}
                />
              )}
            </div>
            <div className={style.autosavedBoxWrap}>
              <div className={style.autosavedBox}>
                <Stack>
                  {isAutosaving ? (
                    <LoadingSpinner size={30} />
                  ) : (
                    <LastSaved lastSaved={lastSaved} />
                  )}
                </Stack>
              </div>
              {enableAudioAssist && false && <AudioAssist />}
              <Button
                customStyleClass={style.submit}
                theme="primary"
                text="Final"
                type="submit"
                disabled={
                  !formik.isValid ||
                  formik.isSubmitting ||
                  isAutosaving ||
                  (!canUpdateManagerReview && formik.values.noteType === 'MANAGER_REVIEW')
                }
              />
            </div>
          </div>
          {context === 'popup' && enableAiScribe && note?.aiScribe?.transcript && (
            <Transcript note={note} />
          )}
          <ModalPopup
            open={showConfirmationModal}
            handleClose={() => setShowConfirmationModal(false)}
          >
            <ConfirmationModalContent
              confirmButtonText="Yes, delete"
              rejectButtonText="No, return to editing"
              title="Are you sure you want to delete your note?"
              customClassInsideContainer={style.deleteNoteModal}
              text=" "
              handleConfirm={deleteNote}
              handleReject={() => setShowConfirmationModal(false)}
            />
          </ModalPopup>
        </form>
      </FormikProvider>
    </Stack>
  );
};

export default NewNoteEdit;
